import React, { useEffect, useState } from 'react';
import { Ban, Check, Flame, KeyRound, Loader2, PartyPopper, PhoneCall, PhoneIncoming, PhoneOff } from 'lucide-react';

import { cn } from '../../../../lib/utils';

// Hot seat transfer (Nic 2026-10-23): right on the "They signed" screen
// while the call is live. Rebuilt 2026-10-29 (Nic: "the business doesn't
// like the idea of blindly transferring"):
//
//   - a light per specialist from RingCentral and the calendar (free, on a
//     call, away, off), polled while the card is up;
//   - Warm (the specialist picks up first, then the hand-over) or Direct
//     (straight to their line), chosen every time;
//   - when their assigned specialist isn't free, or didn't pick up the warm
//     consult, the rep picks another specialist who is free, and that person
//     becomes their onboarding specialist (the server checks the reason).
//
// The rep is never asked how it went: RingCentral says. The client's
// dashboard names their specialist only once the hand-over is real, so the
// Warm script never says a name until they're on.

// Ships Direct only until the warm consult has run live (Nic 2026-10-29:
// one constant, not a setting). Flip to true after the RingCentral test.
export const WARM_TRANSFER_ENABLED = true;

const firstOf = (name) => String(name || '').trim().split(/\s+/)[0] || 'them';

// VA.gov ready when they signed (Nic 2026-10-25): the rep answered Yes in
// Confirm the facts (or, never asked, the intake said Yes). The server's
// twin is vaGovReadyAtSigning (server/lib/hotSeat.js).
export const vaGovReadyAtSigning = (data) => {
  const rep = data?.leadVaGovConfirmed || null;
  if (rep) return rep === 'yes';
  return data?.leadVaGovAccount === 'yes';
};

const DOT = {
  free: 'bg-emerald-500',
  on_call: 'bg-amber-500',
  away: 'bg-slate-400',
  off: 'bg-slate-300 dark:bg-slate-600',
};
const DOT_WORD = { free: 'Free', on_call: 'On a call', away: 'Away', off: 'Off' };

export const AvailabilityDot = ({ availability, className = '' }) => {
  const state = availability?.state || 'free';
  return (
    <span
      role="img"
      aria-label={`${DOT_WORD[state] || state}${availability?.detail && availability.detail !== DOT_WORD[state] ? `: ${availability.detail}` : ''}`}
      title={availability?.detail || DOT_WORD[state]}
      className={cn('inline-block h-2.5 w-2.5 shrink-0 rounded-full', DOT[state] || DOT.free, state === 'on_call' && 'animate-pulse', className)}
    />
  );
};

const Person = ({ entry, muted = false }) => (
  <span className="flex min-w-0 items-center gap-2">
    <AvailabilityDot availability={entry.availability} />
    <span className={cn('truncate text-[15px] font-semibold', muted ? 'text-slate-500 dark:text-slate-400' : 'text-slate-900 dark:text-white')}>
      {entry.name}
    </span>
    <span className="truncate text-[12px] text-slate-500 dark:text-slate-400">{entry.availability?.detail || 'Free'}</span>
  </span>
);

const BTN = 'inline-flex h-10 w-full items-center justify-center gap-1.5 whitespace-nowrap rounded-xl px-4 text-sm font-bold text-white shadow-sm outline-none focus:outline-none focus-visible:ring-2 disabled:cursor-not-allowed disabled:opacity-50';
const BTN_ORANGE = `${BTN} bg-orange-500 hover:bg-orange-600 focus-visible:ring-orange-400/60 dark:hover:!bg-orange-400`;
const BTN_GREEN = `${BTN} bg-emerald-600 hover:bg-emerald-500 focus-visible:ring-emerald-400/60`;
const BTN_QUIET =
  'inline-flex h-10 items-center justify-center gap-1.5 whitespace-nowrap rounded-xl bg-white px-4 text-sm font-semibold text-slate-700 ring-1 ring-slate-200 outline-none hover:bg-slate-50 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50 dark:!bg-white/5 dark:text-slate-200 dark:ring-white/10';

// The seconds a consult has been ringing, for the "Ringing Kit… 12s" line.
const useSecondsSince = (since, on) => {
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!on || !since) {
      setN(0);
      return undefined;
    }
    const tick = () => setN(Math.max(0, Math.round((Date.now() - new Date(since).getTime()) / 1000)));
    tick();
    const t = setInterval(tick, 500);
    return () => clearInterval(t);
  }, [since, on]);
  return n;
};

const HotSeatTransferCard = ({
  specialist = null, // { userId, name }: their assigned onboarding specialist
  // Every specialist with a light: [{ userId, name, extension, assigned, availability }].
  specialists = null,
  // false: VA.gov is their first step (No / I don't know), no hot seat.
  vaGovReady = true,
  // They tapped "I've done it" during the call (Nic 2026-10-25): say so.
  vaGovJustDone = false,
  clientFirstName = null,
  // The live warm consult, from the call: { target, state, since }.
  consult = null,
  onSend, // (target, { mode, redirectFrom, reason })
  onConsultAnswered = null, // the rep heard them pick up
  onCompleteConsult = null,
  onCancelConsult = null,
  sending = false,
  sentTo = null,
  warmEnabled = WARM_TRANSFER_ENABLED,
  // The call is live on the rep's line but NOT in this browser (their cell,
  // the RingCentral app, a desk phone; Nic 2026-11-03, hot transfer from
  // any device): the server moves it, Direct only. A warm handoff needs the
  // rep's audio, so from another device they pick the call up here first.
  offDevice = false,
  // Bring the call into this browser (Nic 2026-11-04): the same switch the
  // pick-up card runs, from here; once it lands, Warm and Direct both work.
  onBringHere = null,
  bringing = false,
}) => {
  const [mode, setMode] = useState(warmEnabled && !offDevice ? 'warm' : 'direct');
  const effectiveMode = offDevice ? 'direct' : mode;
  // The call landing in the browser (Bring the call here) brings the Warm
  // default back; the rep came over for a warm handoff (review 2026-11-04).
  useEffect(() => {
    if (!offDevice) setMode(warmEnabled ? 'warm' : 'direct');
  }, [offDevice, warmEnabled]);
  const [pickedId, setPickedId] = useState(null);
  // Who the last consult failed with: that opens the pick list even when
  // their light reads free.
  const failedWith = consult?.state === 'no_answer' ? consult.target?.userId || null : null;
  const ringingFor = useSecondsSince(consult?.since, consult?.state === 'ringing');
  const client = clientFirstName || 'the veteran';

  if (sentTo) {
    return (
      <div className="mt-6 w-full max-w-xl rounded-2xl bg-orange-50 px-4 py-3 text-left text-sm text-orange-900 ring-1 ring-orange-200/70 dark:!bg-orange-500/10 dark:text-orange-100 dark:ring-orange-400/20">
        <div className="flex items-center gap-2 font-semibold">
          <Flame size={16} className="shrink-0 text-orange-500" /> Sent to {sentTo}.
        </div>
        <div className="mt-0.5 text-[13px] text-orange-800/80 dark:text-orange-200/80">
          If {firstOf(sentTo)} misses it, it&apos;s on {firstOf(sentTo)}&apos;s list to call them back.
        </div>
      </div>
    );
  }
  const first = firstOf(specialist?.name);
  if (!vaGovReady) {
    // VA.gov first (Nic 2026-10-25): their specialist has nothing to do until
    // they finish it, so no transfer. The rep still names who it'll be (that
    // person is final: no hot seat can move them).
    const who = specialist ? first : 'your onboarding specialist';
    return (
      <section
        aria-label="VA.gov first"
        className="mt-6 w-full max-w-xl rounded-2xl bg-white p-4 text-left shadow-[0_1px_2px_rgba(15,23,42,0.04),0_2px_10px_rgba(15,23,42,0.06)] ring-1 ring-sky-200/80 dark:!bg-surface-card dark:ring-sky-400/25"
      >
        <div className="flex items-center gap-2 text-[15px] font-bold text-slate-900 dark:text-white">
          <span className="flex h-7 w-7 items-center justify-center rounded-full bg-sky-600 text-white">
            <KeyRound size={15} />
          </span>
          VA.gov comes first
        </div>
        {specialist ? (
          <>
            <div className="mt-3 text-[11px] font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500">
              Their onboarding specialist
            </div>
            <div className="text-[15px] font-semibold text-slate-900 dark:text-white">{specialist.name}</div>
          </>
        ) : null}
        <p className="mb-3 mt-2 rounded-xl bg-slate-50 px-3 py-2 text-[13px] italic leading-snug text-slate-600 dark:!bg-white/5 dark:text-slate-300">
          Say: &ldquo;{specialist ? `Your onboarding specialist will be ${first}. ` : ''}Your first step is getting into your
          VA.gov account. Your dashboard walks you through it. When you&apos;re in, tap &lsquo;I&apos;ve done it&rsquo; and{' '}
          {who} will reach out to you.&rdquo;
        </p>
        <div className="flex gap-2 rounded-xl bg-amber-50 px-3 py-2 text-[12px] font-medium leading-snug text-amber-900 ring-1 ring-amber-200 dark:!bg-amber-500/10 dark:text-amber-200 dark:ring-amber-400/25">
          <Ban size={14} className="mt-px shrink-0" />
          <span>
            No hot transfer: VA.gov isn&apos;t set up. Please don&apos;t transfer them from RingCentral or any other
            way. Wrap up the call here.
          </span>
        </div>
      </section>
    );
  }

  // The people: the assigned one from the lights when loaded, else what we
  // know (their name, no light yet).
  const list = specialists || [];
  const assignedEntry = specialist
    ? list.find((s) => s.userId === specialist.userId) || { userId: specialist.userId, name: specialist.name, extension: null, assigned: true, availability: null }
    : null;
  const others = list.filter((s) => !specialist || s.userId !== specialist.userId);
  const assignedFree = !assignedEntry?.availability || assignedEntry.availability.state === 'free';
  const showOthers = !!assignedEntry && (!assignedFree || failedWith === assignedEntry.userId);
  const picked = pickedId ? others.find((s) => s.userId === pickedId) || null : null;
  const chosen = picked || assignedEntry;
  const chosenFirst = firstOf(chosen?.name);
  const redirect = picked && assignedEntry ? { redirectFrom: assignedEntry.userId, reason: failedWith === assignedEntry.userId ? 'no_answer' : 'not_available' } : {};
  const target = chosen ? { userId: chosen.userId, name: chosen.name, extension: chosen.extension } : null;
  const canSend = !!target?.extension && !sending && !bringing;
  const live = consult && consult.state !== 'no_answer';

  const sayLine =
    effectiveMode === 'warm'
      ? 'Hold on one moment while I bring your onboarding specialist on the line.'
      : `I'm handing you to ${chosenFirst}, who will be your assigned onboarding specialist. If they don't pick up, don't worry: they'll call you back as soon as they can!`;

  return (
    <section
      aria-label="Hot seat transfer"
      className="mt-6 w-full max-w-xl rounded-2xl bg-white p-4 text-left shadow-[0_1px_2px_rgba(15,23,42,0.04),0_2px_10px_rgba(15,23,42,0.06)] ring-1 ring-orange-200/80 dark:!bg-surface-card dark:ring-orange-400/25"
    >
      <div className="flex items-center gap-2 text-[15px] font-bold text-slate-900 dark:text-white">
        <span className="flex h-7 w-7 items-center justify-center rounded-full bg-orange-500 text-white">
          <Flame size={15} />
        </span>
        Hot seat transfer
      </div>
      {vaGovJustDone ? (
        <div className="mt-3 flex items-start gap-2 rounded-xl bg-emerald-50 px-3 py-2 text-[13px] font-semibold leading-snug text-emerald-800 ring-1 ring-emerald-200 dark:!bg-emerald-500/10 dark:text-emerald-200 dark:ring-emerald-400/25">
          <PartyPopper size={15} className="mt-px shrink-0" />
          <span>
            {clientFirstName || 'They'} just tapped &lsquo;I&apos;ve done it&rsquo; for VA.gov on their dashboard. You can
            hot transfer them now!
          </span>
        </div>
      ) : null}
      {!assignedEntry ? (
        <p className="mb-0 mt-2 text-[13px] leading-snug text-slate-500 dark:text-slate-400">
          Their onboarding specialist is being assigned. They&apos;ll show here in a moment.
        </p>
      ) : (
        <>
          <div className="mt-3 text-[11px] font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500">
            Their onboarding specialist
          </div>
          <Person entry={assignedEntry} />

          {/* On another device: the server sends them straight over. */}
          {offDevice && !live ? (
            <div className="mt-3 rounded-xl bg-sky-50 px-3 py-2 text-[12px] leading-snug text-sky-900 ring-1 ring-sky-200 dark:!bg-sky-500/10 dark:text-sky-100 dark:ring-sky-400/25">
              <div className="flex items-start gap-2">
                <PhoneCall size={14} className="mt-px shrink-0" />
                <span>You&apos;re on this call via your RingCentral app.</span>
              </div>
              {onBringHere ? (
                <button
                  type="button"
                  disabled={bringing || !!sending}
                  onClick={onBringHere}
                  title="Move the call into this browser; Warm and Direct both work once it is here"
                  className={cn(BTN_QUIET, 'mt-2 w-full')}
                >
                  {bringing ? <Loader2 size={14} className="animate-spin" /> : <PhoneIncoming size={14} />}
                  {bringing ? 'Bringing the call here…' : 'Bring the call here (for a warm handoff)'}
                </button>
              ) : null}
            </div>
          ) : null}

          {/* Warm or Direct, chosen every time (Nic 2026-10-29). */}
          {warmEnabled && !live && !offDevice ? (
            <div className="mt-3">
              <div role="radiogroup" aria-label="How to transfer" className="flex gap-1 rounded-lg bg-slate-100 p-0.5 dark:!bg-white/10">
                {[
                  { value: 'warm', label: 'Warm' },
                  { value: 'direct', label: 'Direct' },
                ].map((m) => {
                  const on = mode === m.value;
                  return (
                    <button
                      key={m.value}
                      type="button"
                      role="radio"
                      aria-checked={on}
                      disabled={!!sending}
                      onClick={() => setMode(m.value)}
                      className={cn(
                        'h-7 flex-1 whitespace-nowrap rounded-md text-[12px] font-semibold outline-none focus:outline-none disabled:opacity-50',
                        on
                          ? 'bg-white text-slate-800 shadow-sm dark:!bg-surface-elevated dark:text-slate-100'
                          : 'text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200',
                      )}
                    >
                      {m.label}
                    </button>
                  );
                })}
              </div>
              <div className="mt-1 text-[11px] text-slate-500 dark:text-slate-400">
                {mode === 'warm'
                  ? `${chosenFirst} picks up first, then you hand them over. If ${chosenFirst} doesn't answer, they stay with you.`
                  : `Sends them straight to ${chosenFirst}'s line. If ${chosenFirst} misses it, ${chosenFirst} calls them back.`}
              </div>
            </div>
          ) : null}

          {/* Someone else: only when the assigned one isn't free, or didn't
              pick up the warm consult. Free ones first, the rest shown but
              not pickable. */}
          {showOthers && !live ? (
            <div className="mt-3 rounded-xl bg-slate-50 px-3 py-2.5 dark:!bg-white/5">
              <div className="text-[12px] font-semibold text-slate-700 dark:text-slate-200">
                {failedWith === assignedEntry.userId ? `${first} didn't pick up.` : `${first} isn't free.`} Who should take them?
              </div>
              {others.length ? (
                <div className="mt-2 flex flex-wrap gap-1.5" role="group" aria-label="Pick another specialist">
                  {others.map((s) => {
                    const free = s.availability?.state === 'free';
                    const on = pickedId === s.userId;
                    return (
                      <button
                        key={s.userId}
                        type="button"
                        aria-pressed={on}
                        disabled={!free || !!sending}
                        title={s.availability?.detail || ''}
                        onClick={() => setPickedId(on ? null : s.userId)}
                        className={cn(
                          'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[12px] font-semibold ring-1 outline-none focus:outline-none disabled:cursor-not-allowed disabled:opacity-60',
                          on
                            ? 'bg-slate-900 text-white ring-slate-900 dark:bg-white dark:!text-slate-900 dark:ring-white'
                            : 'bg-white text-slate-700 ring-slate-200 hover:bg-slate-100 dark:!bg-white/10 dark:text-slate-200 dark:ring-white/10',
                        )}
                      >
                        <AvailabilityDot availability={s.availability} />
                        {s.name}
                        {!free ? <span className="font-normal text-slate-400">· {s.availability?.detail}</span> : null}
                      </button>
                    );
                  })}
                </div>
              ) : null}
              {!others.some((s) => s.availability?.state === 'free') ? (
                <div className="mt-1.5 text-[11px] text-slate-500 dark:text-slate-400">
                  Nobody is free right now. {first} keeps them and calls them back.
                </div>
              ) : null}
              {picked ? (
                <div className="mt-1.5 text-[11px] text-slate-500 dark:text-slate-400">
                  {picked.name} becomes their onboarding specialist.
                </div>
              ) : null}
            </div>
          ) : null}

          {!live ? (
            <p className="mb-3 mt-3 rounded-xl bg-slate-50 px-3 py-2 text-[13px] italic leading-snug text-slate-600 dark:!bg-white/5 dark:text-slate-300">
              Say: &ldquo;{sayLine}&rdquo;
            </p>
          ) : null}

          {/* The consult (Warm): the states, in the card. */}
          {live ? (
            <div className="mt-3 rounded-xl bg-amber-50 px-3 py-2.5 ring-1 ring-amber-200 dark:!bg-amber-500/10 dark:ring-amber-400/25" aria-live="polite">
              {consult.state === 'holding' || consult.state === 'dialing' ? (
                <div className="flex items-center gap-2 text-[13px] font-semibold text-amber-900 dark:text-amber-100">
                  <Loader2 size={14} className="animate-spin" />
                  {consult.state === 'holding' ? `Putting ${client} on hold…` : `Calling ${firstOf(consult.target?.name)}…`}
                </div>
              ) : consult.state === 'ringing' ? (
                <>
                  <div className="flex items-center gap-2 text-[13px] font-semibold text-amber-900 dark:text-amber-100">
                    <PhoneCall size={14} className="animate-pulse" />
                    Ringing {firstOf(consult.target?.name)}… {ringingFor}s
                  </div>
                  <div className="mt-0.5 text-[11px] text-amber-800/80 dark:text-amber-200/80">
                    {client} is on hold. If you hear voicemail, hit Cancel.
                  </div>
                  <div className="mt-2 flex gap-2">
                    <button type="button" onClick={onConsultAnswered} className={`${BTN_QUIET} flex-1`}>
                      <Check size={14} /> {firstOf(consult.target?.name)} answered
                    </button>
                    <button type="button" onClick={onCancelConsult} className={`${BTN_QUIET} flex-1`}>
                      <PhoneOff size={14} /> Cancel
                    </button>
                  </div>
                </>
              ) : consult.state === 'talking' ? (
                <>
                  <div className="text-[13px] font-semibold text-amber-900 dark:text-amber-100">
                    {firstOf(consult.target?.name)} is on the line. {client} is on hold.
                  </div>
                  <div className="mt-2 flex gap-2">
                    <button type="button" onClick={onCompleteConsult} className={`${BTN_GREEN} flex-1`}>
                      <Check size={14} /> Complete transfer
                    </button>
                    <button type="button" onClick={onCancelConsult} className={`${BTN_QUIET} flex-1`}>
                      Back to {client}
                    </button>
                  </div>
                </>
              ) : (
                <div className="flex items-center gap-2 text-[13px] font-semibold text-amber-900 dark:text-amber-100">
                  <Loader2 size={14} className="animate-spin" />
                  Joining {firstOf(consult.target?.name)} and {client}…
                </div>
              )}
            </div>
          ) : target?.extension ? (
            <div className="space-y-2">
              <button
                type="button"
                disabled={!canSend}
                onClick={() => onSend?.(target, { mode: effectiveMode, ...(offDevice ? { via: 'server' } : {}), ...redirect })}
                title={
                  effectiveMode === 'warm'
                    ? `Call ${chosen.name} first, then hand them over`
                    : offDevice
                      ? `Send the call on your phone to ${chosen.name} now`
                      : `Send them to ${chosen.name} now`
                }
                className={BTN_ORANGE}
              >
                {sending ? <Loader2 size={14} className="animate-spin" /> : <Flame size={14} />}
                {effectiveMode === 'warm' ? `Bring ${chosenFirst} on` : `Hot Transfer to ${chosenFirst}`}
              </button>
              {failedWith === assignedEntry.userId && !picked ? (
                <div className="text-[11px] text-slate-500 dark:text-slate-400">
                  Or keep them with {first}: wrap up here and {first} calls them back.
                </div>
              ) : null}
            </div>
          ) : (
            <p className="mb-0 text-[13px] leading-snug text-slate-500 dark:text-slate-400">
              {chosenFirst} can&apos;t take a transfer right now (no phone extension set up), so {chosenFirst} will reach out to them.
            </p>
          )}
        </>
      )}
    </section>
  );
};

export default HotSeatTransferCard;
